import { Box } from "@mui/material";
import React, { useContext, useEffect, useState, PropsWithChildren, useRef, useMemo } from "react";
import { useNavigate, useParams, Outlet, useLocation } from "react-router-dom";
import APIRequest, { APIError } from "../../system/APIRequest";
import { currentUser } from "system/InfegyUser";
import { useSnackbar } from "notistack";
import { DashboardPage, DashboardPageInterface } from "./DashboardPage";
import { sortAndCombinePages } from "components/dashboardParts/DashboardPageSelector";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import { useTranslation } from "react-i18next";

const DashboardContext = React.createContext<DashboardDocument | null>(null);
const DashboardProvider = DashboardContext.Provider;

// Use this function to access the dashboard context in child components.
// This will verify that the dashboard exists and is not null, avoiding typescript issues. 
export const useDashboard = () => {
  const dashboardContext = useContext(DashboardContext);
  if (!dashboardContext) {
    throw new Error(
      "useDashboard has to be used within <DashboardContext.Provider>"
    );
  }
  return dashboardContext;
};

interface DashboardPageListOptions {
  dashboardPages: Array<DashboardPageInterface>;
  handleRefreshPageList: (dashboardLink?: string) => void;
}

export const DashboardPageListContext = React.createContext<DashboardPageListOptions | undefined>(undefined);
const DashboardPageListProvider = DashboardPageListContext.Provider;

// Use this function to access the dashboard page list context in child components.
// This will provide access to a sorted list of pages based on the dashboard's page order.
export const useDashboardPageList = () => {
  const dashboardPageListContext = useContext(DashboardPageListContext);
  if (!dashboardPageListContext) {
    throw new Error(
      "useDashboardPageList has to be used within <DashboardPageListProvider>"
    );
  }
  return dashboardPageListContext;
};

export default function Dashboard(props: {}): JSX.Element {
  const { dashboardId, dashboardPageId } = useParams<any>();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const user = currentUser();
  const { i18n } = useTranslation();

  const [dashboardDocument, setDashboardDocument] = useState<DashboardDocument | null>(null);
  const [pages, setPages] = useState<Array<DashboardPage>>([]);

  async function loadDashboard(dashboardId: string): Promise<DashboardDocument | null> {
    let dashboard = null;
    
    try {
      const response: any = await APIRequest(`obj/dashboard/${dashboardId}`, undefined, undefined, false, 3, [404]);
      dashboard = response.output as DashboardDocument;
    } catch (error: any) {
      if((error.code === 401 || error.code === 404) && user === undefined) {
        // Not logged in and need to be
        navigate('/login', { state: {from: location}, replace: true });
      } else if (error.code === 404) {
        // User doesn't have access, or it doesn't exist
        navigate('/notfound');
      } else {
        enqueueSnackbar(`${i18n.t("dashboard.load-dashboard-error")} ${error.message} ${error.server_message}`, { variant: "error", persist: true });
      }
    }

    return dashboard;
  }

  async function loadDashboardPages(dashboardId: string): Promise<Array<DashboardPage>> {
    let pages: Array<DashboardPage> = [];

    try {
      let response: any = await APIRequest(`obj/dashboard/${dashboardId}/entry`);
      if (response.output) {
        pages = Array.isArray(response.output) ? response.output : [response.output];
      }
    } catch (error: any) {
      enqueueSnackbar(i18n.t("dashboard.load-dashboard-error") + error.server_message, { variant: "error", persist: true });
    }
    
    return pages;
  }

  async function createStarterPage(dashboard: DashboardDocument): Promise<string> {
    // Since there is no page, we need to create one.
    const newUUID = self.crypto.randomUUID();
    let newPageId: string = "";
    try {
      const response: any = await APIRequest('obj/dashboard-page/', {}, {
        method: 'POST',
        body: JSON.stringify({
          uuid: newUUID,
          name: "Untitled Page",
          dashboard_id: dashboard.id
        })
      });
      newPageId = response.id;
    } catch (error: any) {
      enqueueSnackbar(i18n.t("dashboard.create-dashboard-error") + error.server_message, { variant: "error", persist: true });
    }

    try {
      if (!dashboard.data.pages) {
        dashboard.data.pages = [];
      }
      dashboard.data.pages.push(newUUID);
      // Save the new page to the dashboard page list
      await APIRequest(`obj/dashboard/${dashboard.id}`, {}, {
        method: 'PUT',
        "body": JSON.stringify({
          data: dashboard.data
        })
      });
    } catch (error: any) {
      enqueueSnackbar(i18n.t("dashboard.edit-dashboard-error") + error.server_message, { variant: "error", persist: true });
    }

    return newPageId;
  }

  function pageExists(pageId: string, pages: Array<DashboardPage>): boolean {
    if (!pages.length) {
      return false;
    }
    return pages.some((page) => page.id === pageId);
  }

  function getFirstPageId(dashboard: DashboardDocument, pages: Array<DashboardPage>): string {
    // The dashboard holds the page order but the database (pages) maintains the page data.
    for (const uuid of dashboard.data.pages) {
      // Start searching for pages based on the dashboard's order.  In theory, the page we need 
      // should always be the first uuid.
      const page = pages.find((page) => {
        return page.uuid === uuid;
      });
      if (page) {
        // We found the page.  Get its id.
        return page.id;
      }
    }
    // We were unable to find the page.
    return "";
  }

  async function load(dashboardId: string) {
    setDashboardDocument(null)
    setPages([]);

    const dashboard = await loadDashboard(dashboardId);
    if (!dashboard) {
      // User doesn't have access, or it doesn't exist
      navigate('/notfound');
      return;
    }

    let pages = await loadDashboardPages(dashboardId);

    if (pages.length === 0 && dashboard._modifiable) {
      await createStarterPage(dashboard);
      pages = await loadDashboardPages(dashboardId);
    }

    pages = sortAndCombinePages(dashboard, pages);

    let activePageId = "";
    if (dashboardPageId && pageExists(dashboardPageId, pages)) {
      // Use the `dashboardPageId` from the URL params.
      activePageId = dashboardPageId;
    }

    if (!activePageId) {
      // Try to find a default first page.
      activePageId = getFirstPageId(dashboard, pages);
    }

    setDashboardDocument(dashboard);
    setPages(pages);

    if (activePageId) {
      // We found the page to use.
      const isAlreadyOnPage = location.pathname.includes(`page/${activePageId}`);
      if (!isAlreadyOnPage) {
        navigate(`page/${activePageId}`, { replace: true });
      }
    } else {
      // We were unable to find a page to use.
      navigate('/notfound');
    }
  }

  useEffect(() => {
    if (dashboardId) {
      load(dashboardId);
    }
  }, [dashboardId, dashboardPageId]);
  
  return (
    <Box>
      {dashboardDocument &&
       <DashboardProvider value={dashboardDocument}>
        <DashboardPageListProvider value={{dashboardPages: pages, handleRefreshPageList: async (navigateLink?: string | undefined) => {
          load(dashboardDocument.id);
          if (navigateLink) {
            navigate(navigateLink);
          }
        }}}>
          <Outlet/>
        </DashboardPageListProvider>
      </DashboardProvider>}
    </Box>
  );
}